// c:\Users\Parashuram\Projects\ei-demand-supply-tool\client\app\src\components\ExcelImport.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import RefreshIcon from "@mui/icons-material/Refresh"
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  TextField,Modal, 
  Paper, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  LinearProgress,
  Chip
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import UploadComponent from "./UploadComponent";
import CloseIcon from '@mui/icons-material/Close';
import DataTable from "./DataTable";
import { UPLOAD_BATCH_SIZE } from "../config/maintenanceConfig";
import { normalizeExcelDates } from "../utils/reportDates";
import { getToken } from "../services/auth";

function ExcelImport() {
  const [selectedType, setSelectedType] = useState("");
  const [selectedFormat, setSelectedFormat] = useState("xlsx");
  const [uploadedData, setUploadedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [uploadLogs, setUploadLogs] = useState([]);
  const [archivePreview, setArchivePreview] = useState(null);
  const [archivePreviewOpen, setArchivePreviewOpen] = useState(false);
  const [archiveError, setArchiveError] = useState("");
  const [archiveLoading, setArchiveLoading] = useState(false);
  const today = new Date().toISOString().split("T")[0]; 
  const [reportExtractionDate, setReportExtractionDate] = useState(today);

  // Upload progress tracking
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, successCount: 0, failedCount: 0 });
  const [failedRecords, setFailedRecords] = useState([]);
  const [failedRecordsModalOpen, setFailedRecordsModalOpen] = useState(false);
  
  // Cancel upload control
  const [uploadCancelled, setUploadCancelled] = useState(false);
  const uploadCancelledRef = React.useRef(false);
  
  // Upload progress modal
  const [uploadProgressModalOpen, setUploadProgressModalOpen] = useState(false);

  // const [reportExtractionDate, setReportExtractionDate] = useState("");
  const [fileUploadsModalOpen, setFileUploadsModalOpen] = useState(false);
const [selectedFileName, setSelectedFileName] = useState("");
const [fileUploads, setFileUploads] = useState([]);
const [paginationData, setPaginationData] = useState({
  page: 1,
  limit: 10,
  total: 0,
  pages: 0
});

  // Get username from localStorage or context (adjust as per your app)
  const username = localStorage.getItem("username") || "Unknown";
  const uploadPath = (root) => `${process.env.REACT_APP_API_BASE_URL}/${root}/${selectedFormat === "csv" ? "uploadCsvAndProcess" : "uploadAndProcess"}`;

  const recordArchivedUpload = async (file) => {
    try {
      const base = `${process.env.REACT_APP_API_BASE_URL}/api/excel-upload-log/archive`;
      const { data } = await axios.post(`${base}/initiate`, {
        importType: selectedType, fileName: file.name, size: file.size,
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      const stored = await fetch(data.uploadUrl, {
        method: "PUT", headers: { "Content-Type": data.contentType }, body: file,
      });
      if (!stored.ok) throw new Error("S3 rejected the file upload.");
      await axios.post(`${base}/complete`, {
        archiveId: data.archiveId, reportExtractionDate: reportExtractionDate || null,
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      await fetchUploadLogs();
    } catch (archiveFailure) {
      console.error("Import completed, but raw file archive failed:", archiveFailure);
      setArchiveError("Import completed, but the raw file could not be archived. Please contact support.");
    }
  };

  const loadArchivePreview = async (id, sheet = "", page = 1) => {
    setArchiveLoading(true);
    setArchiveError("");
    setArchivePreviewOpen(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/excel-upload-log/${id}/preview`,
        { params: { sheet, page, pageSize: 100 }, headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setArchivePreview({ ...data, id });
    } catch (previewFailure) {
      setArchivePreview(null);
      setArchiveError(previewFailure.response?.status === 410
        ? "This file has expired after 30 days and can no longer be viewed or downloaded."
        : previewFailure.response?.data?.error || "Could not load the file.");
    } finally {
      setArchiveLoading(false);
    }
  };

  const downloadArchive = async () => {
    if (!archivePreview) return;
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/excel-upload-log/${archivePreview.id}/download`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      window.location.assign(data.url);
    } catch (downloadFailure) {
      setArchiveError(downloadFailure.response?.status === 410
        ? "This file has expired after 30 days and can no longer be viewed or downloaded."
        : downloadFailure.response?.data?.error || "Could not download the file.");
    }
  };
  
  // Cancel upload handler
  const handleCancelUpload = () => {
    uploadCancelledRef.current = true;
    setUploadCancelled(true);
  };
  
  // Close upload progress modal
  const handleCloseUploadProgressModal = () => {
    if (!loading) {
      setUploadProgressModalOpen(false);
      setUploadProgress({ current: 0, total: 0, successCount: 0, failedCount: 0 });
      setUploadCancelled(false);
      uploadCancelledRef.current = false;
    }
  };

  // Download failed records as Excel
  const downloadFailedRecords = () => {
    if (failedRecords.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(failedRecords.map(r => r.record || r));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Failed Records");
    XLSX.writeFile(wb, `failed_records_${selectedType}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Fetch upload logs on mount and after upload
  const fetchUploadLogs = async () => {
    try {
      const res = await axios.get(process.env.REACT_APP_API_BASE_URL+"/api/excel-upload-log");
      // Fetch employee names for each upload log
      const logsWithNames = await Promise.all(
        res.data.map(async (log) => {
          try {
            const empRes = await axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/employees/getEmployeeById?employeeId=${log.out_uploaded_by}`
            );
            return { ...log, employeeName: empRes.data.employee_name };
          } catch {
            return { ...log, employeeName: log.out_uploaded_by }; // Fallback to ID if not found
          }
        })
      );
      // Sort by upload datetime descending (latest first)
      logsWithNames.sort((a, b) => new Date(b.out_upload_datetime) - new Date(a.out_upload_datetime));
      setUploadLogs(logsWithNames);
    } catch (err) {
      // Optionally handle error
    }
  };

  const fetchUploadsByFileName = async (fileName, page = 1) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/excel-upload-log/by-filename/${fileName}?page=${page}&limit=10`
      );
      // Fetch employee names for each upload
      const uploadsWithNames = await Promise.all(
        res.data.data.map(async (log) => {
          try {
            const empRes = await axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/employees/getEmployeeById?employeeId=${log.out_uploaded_by}`
            );
            return { ...log, employeeName: empRes.data.employee_name };
          } catch {
            return { ...log, employeeName: null }; // Fallback if not found
          }
        })
      );
      setFileUploads(uploadsWithNames);
      setPaginationData(res.data.pagination);
    } catch (err) {
      console.error("Error fetching file uploads:", err);
    }
  };

  const handleFileNameClick = (fileName) => {
    setSelectedFileName(fileName);
    fetchUploadsByFileName(fileName);
    setFileUploadsModalOpen(true);
  };

  const handlePageChange = (event, newPage) => {
    fetchUploadsByFileName(selectedFileName, newPage + 1);
  }; 

  // Fetch upload logs only on mount (not on every state change)
  useEffect(() => {
    fetchUploadLogs();
  }, []);


   

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
    setUploadedData(null); // Clear data when changing type
    setError(null);
    setSuccessMessage(null);
    // Always clear date when type changes
    // setReportExtractionDate("");
  };

  const handleUpload = async (file) => {
    if (!file.name.toLowerCase().endsWith(`.${selectedFormat}`)) {
      setError(`Please select a .${selectedFormat} file.`);
      return;
    }
    setArchiveError("");
    // Validate import type selection
    if (!selectedType) {
      setError("Please select an Import Type before uploading.");
      return;
    }

    if (["Demand", "VCDP", "PDP"].includes(selectedType)) {
      if (!reportExtractionDate) {
        setError("Please select Report Extraction Date before uploading.");
        return;
      }

      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      setFailedRecords([]);
      setUploadCancelled(false);
      uploadCancelledRef.current = false;
      setUploadProgress({ current: 0, total: 0, successCount: 0, failedCount: 0 });

      try {
        const workbook = XLSX.read(await file.arrayBuffer(), { cellDates: true });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!worksheet) throw new Error("The workbook has no sheets.");
        const records = XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: false })
          .map(normalizeExcelDates);
        if (records.length === 0) throw new Error("No data found in file.");

        const total = records.length;
        let processed = 0;
        setUploadProgress({ current: 0, total, successCount: 0, failedCount: 0 });
        setUploadProgressModalOpen(true);
        const endpoint = selectedType.toLowerCase();

        for (let start = 0; start < total; start += UPLOAD_BATCH_SIZE) {
          if (uploadCancelledRef.current) break;
          const batch = records.slice(start, start + UPLOAD_BATCH_SIZE);
          try {
            const response = await axios.post(
              uploadPath(endpoint),
              {
                records: batch,
                report_extraction_date: reportExtractionDate,
                truncate: start === 0,
                finalize: start + batch.length === total,
                sourceFileName: file.name,
              }
            );
            const failed = response.data.results?.failedInserts || 0;
            if (failed) throw new Error(`${failed} records failed in this batch. Upload stopped before finalization.`);
            processed += batch.length;
            setUploadProgress({ current: processed, total, successCount: processed, failedCount: 0 });
          } catch (batchError) {
            setFailedRecords(batch.map((record, index) => ({
              record,
              rowIndex: start + index + 2,
              error: batchError.response?.data?.error || batchError.message,
            })));
            setUploadProgress({ current: start + batch.length, total, successCount: processed, failedCount: batch.length });
            throw batchError;
          }
        }

        if (uploadCancelledRef.current) {
          setSuccessMessage("Upload cancelled before processing was finalized.");
          return;
        }

        await recordArchivedUpload(file);
        const keys = Object.keys(records[0]);
        setUploadedData({
          columns: keys.map((key) => ({ field: key, headerName: key, width: 150 })),
          rows: records,
        });
        setSuccessMessage(`Successfully uploaded ${total} ${selectedType} records.`);
      } catch (uploadError) {
        console.error(`Error uploading ${selectedType}:`, uploadError);
        setError(uploadError.response?.data?.error || uploadError.message || "Upload failed.");
      } finally {
        setLoading(false);
      }
    } else if (selectedType === "Lateral Hiring") {
      if (!reportExtractionDate) {
        setError("Please select Report Extraction Date before uploading.");
        return;
      }

      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("report_extraction_date", reportExtractionDate);

        const endpoint = "lateralHiring";
        const response = await axios.post(
          uploadPath(endpoint),
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        await recordArchivedUpload(file);

        // Display success message from the server
        if (response.data.message) {
          setSuccessMessage(response.data.message);
        }
        // Fetch the updated data after upload
        const baseURL = process.env.REACT_APP_API_BASE_URL; //Deepak Gauniyal - variable host name change. 
        const dataResponse = await axios.get(
          `${baseURL}/${endpoint}`
        );
        const data = dataResponse.data;

        if (data.length > 0) {
          const keys = Object.keys(data[0]);
          const columns = keys.map((key) => ({
            field: key,
            headerName: key.replace(/([A-Z])/g, " $1").trim(), // Add space before capital letters for better readability
            width: 150,
          }));

          setUploadedData({
            columns,
            rows: data,
          });
        } else {
          setUploadedData({
            columns: [],
            rows: [],
          });
          setSuccessMessage("File processed successfully, but no data found.");
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        setError(
          "Error uploading file. Please check the file format and try again."
        );
        if (
          error.response &&
          error.response.data &&
          error.response.data.details
        ) {
          setError(error.response.data.details);
        }
      } finally {
        setLoading(false);
      }
    } else if (selectedType === "Unique Allocation Report") {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      try {
        // Read Excel file and extract only required columns
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        // Only keep required columns
        const filteredData = jsonData
          .map((row) => ({
            assoId:
              row["Asso Id"] || row["AssoId"] || row["Associate Id"] || "",
            assoName: row["Asso Name"] || row["Associate Name"] || "",
            grade: row["Grade"] || "",
          }))
          .filter((row) => row.assoId && row.assoName && row.grade);
        if (filteredData.length === 0) {
          setUploadedData({ columns: [], rows: [] });
          setSuccessMessage("No valid data found in file.");
          setLoading(false);
          return;
        }
        // Send to backend in batches of 500 to avoid PayloadTooLargeError
        const BATCH_SIZE = 1000;
        const totalBatches = Math.ceil(filteredData.length / BATCH_SIZE);
        let lastResponse = null;
        for (let i = 0; i < totalBatches; i++) {
          const batch = filteredData.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE);
          setSuccessMessage(`Uploading batch ${i + 1} of ${totalBatches} (${batch.length} records)...`);
          lastResponse = await axios.post(
            uploadPath("unique-allocation"),
            { records: batch, sourceFileName: file.name }
          );
        }
        await recordArchivedUpload(file);
        if (lastResponse && lastResponse.data.message) {
          setSuccessMessage(`All ${filteredData.length} records uploaded successfully in ${totalBatches} batch(es).`);
        }
        setUploadedData({
          columns: [
            { field: "assoId", headerName: "Asso Id", width: 150 },
            { field: "assoName", headerName: "Asso Name", width: 200 },
            { field: "grade", headerName: "Grade", width: 100 },
          ],
          rows: filteredData,
        });
      } catch (error) {
        setError(
          "Error uploading file. Please check the file format and try again."
        );
        console.error("Error uploading unique allocation report:", error);
      } finally {
        setLoading(false);
      }
    } else if (selectedType === "Rotation List") {
      // For Rotation List, ensure reportExtractionDate is provided
      if (!reportExtractionDate) {
        setError("Please select Report Extraction Date before uploading.");
        return;
      }

      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      setFailedRecords([]);
      setUploadProgress({ current: 0, total: 0, successCount: 0, failedCount: 0 });
      
      // Reset cancel state
      setUploadCancelled(false);
      uploadCancelledRef.current = false;

      // Try V2 batch upload first, fallback to V1 if it fails
      let useV2 = true;
      const baseURI = process.env.REACT_APP_API_BASE_URL;

      try {
        // Read Excel file and extract all columns.
        // cellDates: true converts Excel serial date numbers (e.g. 46033) into
        // JS Date objects; normalizeExcelDates then turns them into YYYY-MM-DD
        // strings so we never send/show a raw serial number.
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils
          .sheet_to_json(worksheet, { defval: "" })
          .map(normalizeExcelDates);

        if (jsonData.length === 0) {
          setUploadedData({ columns: [], rows: [] });
          setSuccessMessage("No data found in file.");
          setLoading(false);
          return;
        }

        const totalRecords = jsonData.length;
        const totalBatches = Math.ceil(totalRecords / UPLOAD_BATCH_SIZE);
        let successCount = 0;
        let failedCount = 0;
        const allFailedRecords = [];

        setUploadProgress({ current: 0, total: totalRecords, successCount: 0, failedCount: 0 });
        setUploadProgressModalOpen(true); // Open the progress modal

        // Try V2 endpoint first
        try {
          for (let i = 0; i < totalBatches; i++) {
            // Check if upload was cancelled
            if (uploadCancelledRef.current) {
              break;
            }
            
            const batchStart = i * UPLOAD_BATCH_SIZE;
            const batchEnd = Math.min((i + 1) * UPLOAD_BATCH_SIZE, totalRecords);
            const batch = jsonData.slice(batchStart, batchEnd);
            
            // V2 API flags:
            // - truncate: true only for first batch (clears stage table)
            // - finalize: true only for last batch (runs transform stored procedure)
            // - line_number_offset: keeps line numbers continuous across batches
            const isFirstBatch = i === 0;
            const isLastBatch = i === totalBatches - 1;

            try {
              await axios.post(
                uploadPath("rotation-v2"),
                { 
                  records: batch,
                  report_extraction_date: reportExtractionDate,
                  username: username,
                  truncate: isFirstBatch,
                  finalize: isLastBatch && !uploadCancelledRef.current, // Don't finalize if cancelled
                  line_number_offset: batchStart,
                  sourceFileName: file.name,
                }
              );

              // V2 API returns { message: "Successfully imported X records." }
              // All records in batch are considered successful if no error thrown
              successCount += batch.length;

              setUploadProgress({ 
                current: batchEnd, 
                total: totalRecords, 
                successCount, 
                failedCount 
              });

            } catch (batchError) {
              console.error(`Batch ${i + 1} failed:`, batchError);
              // Mark entire batch as failed
              failedCount += batch.length;
              allFailedRecords.push(...batch.map((record, idx) => ({
                record,
                error: batchError.response?.data?.message || batchError.message || 'Batch upload failed',
                rowIndex: batchStart + idx + 1,
                batchNumber: i + 1
              })));
              setUploadProgress({ 
                current: batchEnd, 
                total: totalRecords, 
                successCount, 
                failedCount 
              });
            }
          }

          // V2 completed (even if some batches failed or was cancelled)
          setFailedRecords(allFailedRecords);

          // Log the upload only if some records were uploaded
          if (successCount > 0) {
            await recordArchivedUpload(file);
          }

          // Success message is now shown in the modal, no need for inline message

          // Build columns from first record
          const keys = Object.keys(jsonData[0]);
          const columns = keys.map((key) => ({
            field: key,
            headerName: key.replace(/([A-Z])/g, " $1").trim(),
            width: 150,
          }));

          setUploadedData({
            columns,
            rows: jsonData,
          });

        } catch (v2Error) {
          // V2 completely failed, try V1 fallback
          console.error("V2 upload failed, falling back to V1:", v2Error);
          useV2 = false;
          setUploadProgressModalOpen(false); // Close progress modal on V2 failure
          setSuccessMessage("V2 batch upload failed. Trying V1 (file upload)...");
          setFailedRecords([]);
          setUploadProgress({ current: 0, total: 0, successCount: 0, failedCount: 0 });
        }
      } catch (parseError) {
        console.error("Error parsing Excel file:", parseError);
        useV2 = false;
        setSuccessMessage("Could not parse Excel for V2. Trying V1 (file upload)...");
      }

      // V1 Fallback - Original file upload approach
      if (!useV2) {
        try {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("report_extraction_date", reportExtractionDate);
          formData.append("username", username);
          
          const response = await axios.post(
            uploadPath("rotation"),
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          await recordArchivedUpload(file);

          // Display success message from the server
          if (response.data.message) {
            setSuccessMessage(`(V1 Fallback) ${response.data.message}`);
          }
          
          // Fetch the updated data after upload
          const dataResponse = await axios.get(`${baseURI}/rotation`);
          const data = dataResponse.data;

          if (data.length > 0) {
            const keys = Object.keys(data[0]);
            const columns = keys.map((key) => ({
              field: key,
              headerName: key.replace(/([A-Z])/g, " $1").trim(),
              width: 150,
            }));

            setUploadedData({
              columns,
              rows: data,
            });
          } else {
            setUploadedData({
              columns: [],
              rows: [],
            });
            setSuccessMessage("File processed successfully, but no data found.");
          }
        } catch (v1Error) {
          console.error("V1 fallback also failed:", v1Error);
          setError(
            "Error uploading file. Both V2 and V1 methods failed. Please check the file format and try again."
          );
          if (v1Error.response?.data?.details) {
            setError(v1Error.response.data.details);
          }
        }
      }

      setLoading(false);
    }else if (selectedType === "NBL") {
      // For NBL, ensure reportExtractionDate is provided
      if (!reportExtractionDate) {
        setError("Please select Report Extraction Date before uploading.");
        return;
      }
    
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("report_extraction_date", reportExtractionDate);
        formData.append("username", username);
        
        const response = await axios.post(
          uploadPath("nbl"),
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
    
        await recordArchivedUpload(file);
    
        // Display success message from the server
        if (response.data.message) {
          setSuccessMessage(response.data.message);
        }
        
        // Fetch the updated data after upload
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        const dataResponse = await axios.get(
          `${baseURL}/nbl`
        );
        const data = dataResponse.data;
    
        if (data.length > 0) {
          const keys = Object.keys(data[0]);
          const columns = keys.map((key) => ({
            field: key,
            headerName: key.replace(/([A-Z])/g, " $1").trim(),
            width: 150,
          }));
    
          setUploadedData({
            columns,
            rows: data,
          });
        } else {
          setUploadedData({
            columns: [],
            rows: [],
          });
          setSuccessMessage("File processed successfully, but no data found.");
        }
      } catch (error) {
        console.error("Error uploading NBL:", error);
        setError(
          "Error uploading file. Please check the file format and try again."
        );
        if (
          error.response &&
          error.response.data &&
          error.response.data.details
        ) {
          setError(error.response.data.details);
        }
      } finally {
        setLoading(false);
      }
    } else {
      setUploadedData(null);
      setError("This file type is not yet supported.");
    }
  };
  const primaryColor = "#005EB8"; // Cognizant's primary blue
  
  // Generate unique row ID from available fields or use index as fallback
  const getRowId = (row, index) => {
    // Try common ID fields first
    if (row.assoId) return row.assoId;
    if (row.import_id) return row.import_id;
    if (row.pdp_main_id) return row.pdp_main_id;
    if (row.vcdp_main_id) return row.vcdp_main_id;
    if (row.rotation_id) return row.rotation_id;
    if (row.nbl_id) return row.nbl_id;
    if (row.id) return row.id;
    if (row["PDL ID"]) return `pdl_${row["PDL ID"]}_${index}`;
    if (row["Employee ID"] || row["Employee Id"]) return row["Employee ID"] || row["Employee Id"];
    if (row["Asso Id"]) return row["Asso Id"];
    // Fallback: use index (will be added in the mapping)
    return `row_${index}`;
  };

  // Prepare rows with guaranteed unique IDs
  const prepareRowsWithIds = (rows) => {
    return rows.map((row, index) => ({
      ...row,
      _rowId: getRowId(row, index)
    }));
  };

  return (
    
    <Box sx={{ padding: 2, height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      {/* Top Row: Excel Import controls */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 3, flexWrap: 'wrap' }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            color: primaryColor,
            whiteSpace: 'nowrap',
          }}
        >
          Excel Import
        </Typography>
        <FormControl sx={{ minWidth: 200 }} required size="small">
          <InputLabel id="import-type-label">Import Type</InputLabel>
          <Select
            labelId="import-type-label"
            value={selectedType}
            label="Import Type"
            onChange={handleTypeChange}
            size="small"
          >
            <MenuItem value="" disabled>Select Import Type</MenuItem>
            <MenuItem value="Demand">Demand</MenuItem>
            <MenuItem value="PDP">PDP</MenuItem>
            <MenuItem value="VCDP">VCDP</MenuItem>
            <MenuItem value="Rotation List">Rotation List</MenuItem>
            <MenuItem value="Lateral Hiring">Lateral Hiring</MenuItem>
            <MenuItem value="NBL">NBL</MenuItem>
            <MenuItem value="Unique Allocation Report">Unique Allocation Report</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 130 }} size="small">
          <InputLabel id="import-format-label">File Format</InputLabel>
          <Select
            labelId="import-format-label"
            value={selectedFormat}
            label="File Format"
            onChange={(event) => setSelectedFormat(event.target.value)}
            disabled={loading}
          >
            <MenuItem value="xlsx">XLSX</MenuItem>
            <MenuItem value="csv">CSV</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Extraction Date"
          type="date"
          value={reportExtractionDate}
          onChange={(e) => setReportExtractionDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ width: 160 }}
        />

        <UploadComponent key={selectedFormat} onUpload={handleUpload} disabled={loading} format={selectedFormat} />

        {error && (
          <Alert severity="error" sx={{ py: 0, fontSize: '0.85rem' }}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ py: 0, fontSize: '0.85rem' }}>
            {successMessage}
          </Alert>
        )}
        {archiveError && !archivePreviewOpen && <Alert severity="warning">{archiveError}</Alert>}
        {/* Failed Records Button */}
        {failedRecords.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<ErrorOutlineIcon />}
            onClick={() => setFailedRecordsModalOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            View Failed ({failedRecords.length})
          </Button>
        )}
      </Box>

      {/* Upload Progress Modal */}
      <Modal
        open={uploadProgressModalOpen}
        onClose={handleCloseUploadProgressModal}
        aria-labelledby="upload-progress-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {loading ? 'Uploading Data...' : (uploadCancelled ? 'Upload Cancelled' : 'Upload Complete')}
            </Typography>
            {!loading && (
              <IconButton onClick={handleCloseUploadProgressModal} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {loading 
                  ? `Processing: ${uploadProgress.current} / ${uploadProgress.total} records`
                  : `Processed: ${uploadProgress.current} / ${uploadProgress.total} records`
                }
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip 
                  label={`✓ ${uploadProgress.successCount}`} 
                  size="small" 
                  color="success" 
                  variant="outlined"
                />
                {uploadProgress.failedCount > 0 && (
                  <Chip 
                    label={`✗ ${uploadProgress.failedCount}`} 
                    size="small" 
                    color="error" 
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : 0} 
              sx={{ height: 10, borderRadius: 5 }}
              color={uploadCancelled ? 'warning' : (uploadProgress.failedCount > 0 ? 'error' : 'primary')}
            />
          </Box>
          
          {/* Status Messages */}
          {!loading && uploadProgress.successCount > 0 && uploadProgress.failedCount === 0 && !uploadCancelled && (
            <Alert severity="success" sx={{ mb: 2 }}>
              All {uploadProgress.successCount} records uploaded successfully!
            </Alert>
          )}
          {!loading && uploadProgress.failedCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {uploadProgress.successCount} records succeeded, {uploadProgress.failedCount} failed.
            </Alert>
          )}
          {!loading && uploadCancelled && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {["Demand", "VCDP", "PDP"].includes(selectedType)
                ? `Upload was cancelled. ${uploadProgress.successCount} records were staged, but the report was not finalized.`
                : `Upload was cancelled. ${uploadProgress.successCount} records were saved before cancellation.`}
            </Alert>
          )}
          
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            {loading && (
              <Button 
                onClick={handleCancelUpload} 
                color="error" 
                variant="outlined"
                disabled={uploadCancelled}
              >
                {uploadCancelled ? 'Cancelling...' : 'Cancel Upload'}
              </Button>
            )}
            {!loading && uploadProgress.failedCount > 0 && (
              <Button 
                onClick={downloadFailedRecords} 
                color="error" 
                variant="outlined"
                startIcon={<DownloadIcon />}
              >
                Download Failed ({uploadProgress.failedCount})
              </Button>
            )}
            {!loading && (
              <Button 
                onClick={handleCloseUploadProgressModal} 
                variant="contained"
              >
                Close
              </Button>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Table Section */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor, mb: 1, display: 'flex', alignItems: 'center' }}>
          Recent File Imports
          <IconButton color="primary" aria-label="refresh upload logs" onClick={fetchUploadLogs} size="small" sx={{ ml: 1 }}> 
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataTable
            rows={uploadLogs.map((log) => ({
              id: log.out_id,
              fileName: log.out_file_name,
              originalFileName: log.originalFileName,
              uploadedBy: log.employeeName || log.out_uploaded_by,
              uploadedAt: log.out_upload_datetime
                ? new Date(log.out_upload_datetime).toLocaleString()
                : "",
              uploadedAtRaw: log.out_upload_datetime,
              extractionDate: log.out_report_ext_date
                ? new Date(log.out_report_ext_date).toLocaleDateString()
                : "",
            }))}
            columns={[
              { field: "fileName", headerName: "Import Type", flex: 1, minWidth: 120 },
              {
                field: "originalFileName", headerName: "Uploaded File", flex: 1.4, minWidth: 170,
                renderCell: (params) => params.value
                  ? <Button size="small" sx={{ textTransform: "none", justifyContent: "flex-start" }} onClick={() => loadArchivePreview(params.row.id)}>{params.value}</Button>
                  : "—",
              },
              { field: "uploadedBy", headerName: "Uploaded By", flex: 1.2, minWidth: 140 },
              { field: "extractionDate", headerName: "Extraction Date", flex: 1, minWidth: 110 },
              { 
                field: "uploadedAt", 
                headerName: "Latest Upload", 
                flex: 1.3, 
                minWidth: 160,
                renderCell: (params) => {
                  const uploadDate = params.row.uploadedAtRaw ? new Date(params.row.uploadedAtRaw) : null;
                  const isToday = uploadDate && uploadDate.toDateString() === new Date().toDateString();
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        width: '100%',
                        height: '100%',
                        px: 1,
                        backgroundColor: isToday ? '#d4edda' : 'transparent',
                        borderRadius: isToday ? '4px' : 0,
                      }}
                    >
                      <span>{params.value}</span>
                      {isToday && (
                        <DoneAllIcon 
                          sx={{ 
                            fontSize: 16, 
                            color: '#34B7F1',
                            ml: 'auto'
                          }} 
                        />
                      )}
                    </Box>
                  );
                },
              },
              {
                field: "history",
                headerName: "History",
                width: 130,
                sortable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => handleFileNameClick(params.row.fileName)}
                    title="View upload history"
                  >
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                ),
              },
            ]}
            getRowId={(row) => row.id}
            disableSearch={true}
            height="100%"
            hideFooter={true}
          />
        </Box>
      </Box>

<Modal
  open={fileUploadsModalOpen}
  onClose={() => setFileUploadsModalOpen(false)}
  aria-labelledby="file-uploads-modal-title"
  sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}
>
  <Paper 
    sx={{ 
      width: '80%', 
      maxWidth: 800, 
      maxHeight: '90vh',
      p: 3,
      position: 'relative',
      overflow: 'auto'
    }}
  >
    <IconButton
      aria-label="close"
      onClick={() => setFileUploadsModalOpen(false)}
      sx={{
        position: 'absolute',
        right: 8,
        top: 8,
      }}
    >
      <CloseIcon />
    </IconButton>
    
    <Typography variant="h6" id="file-uploads-modal-title" gutterBottom>
      Upload History for {selectedFileName}
    </Typography>
    
    <DataTable
      rows={fileUploads.map((log) => ({
        id: log.id || log.out_id || Math.random().toString(), // Fallback to ensure there's always an id
        uploadedBy: log.employeeName 
          ? `${log.employeeName} (${log.out_uploaded_by})` 
          : log.uploaded_by || log.out_uploaded_by || "",
        uploadedAt: log.out_upload_datetime
          ? new Date(log.out_upload_datetime).toLocaleString()
          : "",
        extractionDate: log.out_report_ext_date
          ? new Date(log.out_report_ext_date).toLocaleDateString()
          : "",
        originalFileName: log.originalFileName,
      }))}
      
      columns={[
        {
          field: "originalFileName", headerName: "Uploaded File", width: 210,
          renderCell: (params) => params.value
            ? <Button size="small" sx={{ textTransform: "none" }} onClick={() => loadArchivePreview(params.row.id)}>{params.value}</Button>
            : "—",
        },
        { field: "uploadedBy", headerName: "Uploaded By", width: 200 },
        { field: "uploadedAt", headerName: "Upload Time", width: 180 },
        { field: "extractionDate", headerName: "Report Extraction Date", width: 180 },
      ]}
      getRowId={(row) => row.id}
      disableSearch={true}
      height={300}
      hideFooter={true}
    />
    
    <TablePagination
      component="div"
      count={paginationData.total}
      page={paginationData.page - 1}
      onPageChange={handlePageChange}
      rowsPerPage={paginationData.limit}
      rowsPerPageOptions={[10]}
      onRowsPerPageChange={() => {}}
    />
  </Paper>
</Modal>

<Modal
  open={archivePreviewOpen}
  onClose={() => { setArchivePreviewOpen(false); setArchiveError(""); }}
  aria-labelledby="archive-preview-title"
  sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
>
  <Paper sx={{ width: "94vw", maxWidth: 1200, maxHeight: "90vh", p: 3, overflow: "auto" }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2 }}>
      <Typography id="archive-preview-title" variant="h6">
        {archivePreview?.fileName || "Uploaded file"}
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        {archivePreview && <Button variant="contained" startIcon={<DownloadIcon />} onClick={downloadArchive}>Download raw file</Button>}
        <IconButton aria-label="Close preview" onClick={() => { setArchivePreviewOpen(false); setArchiveError(""); }}><CloseIcon /></IconButton>
      </Box>
    </Box>
    {archiveError && <Alert severity="warning" sx={{ mb: 2 }}>{archiveError}</Alert>}
    {archiveLoading && <LinearProgress sx={{ mb: 2 }} />}
    {archivePreview && <>
      {archivePreview.sheets.length > 1 && <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
        <InputLabel id="archive-sheet-label">Sheet</InputLabel>
        <Select labelId="archive-sheet-label" value={archivePreview.sheet} label="Sheet"
          onChange={(event) => loadArchivePreview(archivePreview.id, event.target.value, 1)}>
          {archivePreview.sheets.map((sheet) => <MenuItem key={sheet} value={sheet}>{sheet}</MenuItem>)}
        </Select>
      </FormControl>}
      <TableContainer sx={{ maxHeight: "60vh", border: "1px solid #dde5eb" }}>
        <Table stickyHeader size="small">
          <TableHead><TableRow>
            <TableCell>#</TableCell>
            {Array.from({ length: Math.max(0, ...archivePreview.rows.map((row) => row.length)) }, (_, index) =>
              <TableCell key={index}>Column {index + 1}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {archivePreview.rows.map((row, rowIndex) => <TableRow key={rowIndex}>
              <TableCell>{(archivePreview.page - 1) * archivePreview.pageSize + rowIndex + 1}</TableCell>
              {Array.from({ length: Math.max(0, ...archivePreview.rows.map((entry) => entry.length)) }, (_, index) =>
                <TableCell key={index}>{String(row[index] ?? "")}</TableCell>)}
            </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={archivePreview.totalRows}
        page={archivePreview.page - 1} rowsPerPage={archivePreview.pageSize}
        rowsPerPageOptions={[100]}
        onPageChange={(_, page) => loadArchivePreview(archivePreview.id, archivePreview.sheet, page + 1)}
        onRowsPerPageChange={() => {}} />
    </>}
  </Paper>
</Modal>

{/* Failed Records Modal */}
<Modal
  open={failedRecordsModalOpen}
  onClose={() => setFailedRecordsModalOpen(false)}
  aria-labelledby="failed-records-modal-title"
  sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}
>
  <Paper 
    sx={{ 
      width: '90%', 
      maxWidth: 1000, 
      maxHeight: '90vh',
      p: 3,
      position: 'relative',
      overflow: 'auto'
    }}
  >
    <IconButton
      aria-label="close"
      onClick={() => setFailedRecordsModalOpen(false)}
      sx={{
        position: 'absolute',
        right: 8,
        top: 8,
      }}
    >
      <CloseIcon />
    </IconButton>
    
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography variant="h6" id="failed-records-modal-title">
        Failed Records ({failedRecords.length})
      </Typography>
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<DownloadIcon />}
        onClick={downloadFailedRecords}
      >
        Download Failed Records
      </Button>
    </Box>
    
    <Alert severity="info" sx={{ mb: 2 }}>
      These records failed to upload. You can download them, fix the issues, and re-upload.
    </Alert>
    
    <DataTable
      rows={failedRecords.map((item, index) => ({
        id: index,
        rowIndex: item.rowIndex || index + 1,
        batchNumber: item.batchNumber || '-',
        error: item.error || 'Unknown error',
        // Include first few columns from the record for identification
        ...(item.record ? {
          col1: Object.values(item.record)[0] || '',
          col2: Object.values(item.record)[1] || '',
          col3: Object.values(item.record)[2] || '',
        } : {
          col1: Object.values(item)[0] || '',
          col2: Object.values(item)[1] || '',
          col3: Object.values(item)[2] || '',
        })
      }))}
      columns={[
        { field: "rowIndex", headerName: "Row #", width: 70 },
        { field: "batchNumber", headerName: "Batch", width: 70 },
        { field: "col1", headerName: "Column 1", width: 150 },
        { field: "col2", headerName: "Column 2", width: 150 },
        { field: "col3", headerName: "Column 3", width: 150 },
        { field: "error", headerName: "Error", flex: 1, minWidth: 200 },
      ]}
      getRowId={(row) => row.id}
      disableSearch={true}
      height={400}
      hideFooter={false}
    />
  </Paper>
</Modal>

      {/* Bottom: Uploaded Data Grid - only show when data exists */}
      {uploadedData && uploadedData.rows.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor, mb: 1 }}>
            Uploaded Data Preview
          </Typography>
          <DataTable
            rows={uploadedData.rows}
            columns={uploadedData.columns}
            getRowId={getRowId}
            height={300}
          />
        </Box>
      )}
      {uploadedData && uploadedData.rows.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No data to display.
        </Alert>
      )}
    </Box>
  );
}

export default ExcelImport;
